import { z } from "zod";

/**
 * Vỏ lỗi chuẩn của hub.
 *
 * `code` là thứ MÁY đọc và ổn định qua các phiên bản; `message` là tiếng Việt cho người. Giao diện
 * **không bao giờ** được phân nhánh theo `message` — đổi một câu chữ mà làm hỏng một nhánh xử lý là
 * loại lỗi không ai nghĩ tới lúc sửa.
 */
export const HQ_ERROR_CODES = [
  /** Thiếu quyền. Kèm `details.required` là chuỗi quyền còn thiếu. */
  "HQ_PERMISSION_DENIED",
  /** Phiên hợp lệ nhưng đang bị chặn tới khi đổi mật khẩu tạm. */
  "HQ_PASSWORD_CHANGE_REQUIRED",
  /** Chưa đăng nhập, hoặc phiên đã bị thu hồi. */
  "HQ_UNAUTHENTICATED",
  /**
   * PLAT-2026-118 · A7 — thử sai quá nhiều lần (đăng nhập / mật khẩu hiện tại khi đổi mật khẩu). Kèm
   * `details.retryAfterSeconds` và header `Retry-After`. Giao diện hiện `message` như mọi lỗi khác.
   */
  "HQ_RATE_LIMITED",

  /** Cùng `Idempotency-Key` nhưng thân yêu cầu KHÁC — xem `common/idempotency.ts`. */
  "HQ_IDEMPOTENCY_CONFLICT",
  /** Đã có lượt chạy cùng `(nguồn, luồng)`. KHÔNG phải lỗi: kèm `details.runId` đang chạy. */
  "HQ_ALREADY_RUNNING",

  "HQ_CONNECTOR_NOT_FOUND",
  /** `metricKey` không có trong Metric Registry. */
  "HQ_METRIC_NOT_FOUND",
  /** Chiều lọc không nằm trong `allowedDimensions` của metric đó. */
  "HQ_DIMENSION_NOT_ALLOWED",
  /** Hợp đồng schema của nguồn không còn đúng — hub từ chối trả số mới. */
  "HQ_SCHEMA_DRIFT",

  "HQ_VALIDATION_ERROR",
  /**
   * HQ-2026-008 · ADR-004 — bật một gán app ⇄ bộ thông số khi bộ CHƯA chạy thử đạt (`test_status ≠ passed`). Giao diện
   * dẫn người dùng tới nút « Chạy thử » thay vì hiện một câu chung chung.
   */
  "HQ_PARTNER_SET_UNTESTED",
  /**
   * PLAT-2026-141 · ADR-041 D13 (dùng từ GĐ3) — gán / tuyến trỏ một connection `purpose = lab`. CSDL cũng từ chối (khoá ngoại
   * ghép, I1); mã này để giao diện nói đúng « kết nối thử không phục vụ app » thay vì lỗi chung.
   */
  "HQ_CONNECTION_PURPOSE_LAB",
  /** Đổi trường bất biến của account / connection / version (purpose, sản phẩm, loại, môi trường, payload version — I3, I5). */
  "HQ_CONNECTION_IMMUTABLE_FIELD",
  /** Kích hoạt connection bằng version chưa chứng nhận (I7) — giao diện dẫn tới « Chạy thử ». Thay `HQ_PARTNER_SET_UNTESTED` ở GĐ3. */
  "HQ_CONNECTION_VERSION_NOT_CERTIFIED",
  /** Đang chuyển dữ liệu tích hợp (ADR-041 D15): mọi thao tác GHI cấu hình tích hợp tạm khoá; đọc vẫn chạy. */
  "HQ_MIGRATION_IN_PROGRESS",
  /** PLAT-2026-141 GĐ2B — phòng thử từ chối chạy bằng fixture (rào live, ADR-041 D4). `details.reason` ∈ `LAB_GUARD_REASONS`. */
  "HQ_LAB_BLOCKED",
  "HQ_NOT_FOUND",
  "HQ_CONFLICT",
  /**
   * Một app NGUỒN mà hub gọi qua HTTP không trả lời được — HQ-2026-002.
   *
   * Phân biệt với `HQ_INTERNAL` vì hai câu này dẫn tới hai hành động khác nhau: `HQ_INTERNAL` nghĩa
   * là « hub hỏng, báo người viết hub »; mã này nghĩa là « hub ổn, app kia chưa trả lời — thử lại,
   * hoặc đi kiểm app kia ». Gộp chúng thì mọi lượt bube-shop deploy lại đều trông như hub hỏng.
   *
   * Cũng là mã trả về khi hub CHƯA ĐƯỢC CẤU HÌNH để gọi app đó. « Chưa cấu hình » và « gọi không
   * được » cùng một hành động với người dùng (không làm được, đi tìm người vận hành) và cố ý không
   * phân biệt với người gọi: một mã riêng cho « thiếu secret » là một máy dò cấu hình miễn phí.
   */
  "HQ_UPSTREAM_UNAVAILABLE",
  "HQ_INTERNAL",
] as const;

export const HqErrorCodeSchema = z.enum(HQ_ERROR_CODES);
export type HqErrorCode = z.infer<typeof HqErrorCodeSchema>;

export const ApiErrorSchema = z.object({
  error: z.object({
    code: HqErrorCodeSchema,
    /** Tiếng Việt, cho người đọc. Giao diện KHÔNG phân nhánh theo trường này. */
    message: z.string(),
    requestId: z.string(),
    details: z.unknown().optional(),
  }),
});

export type ApiError = z.infer<typeof ApiErrorSchema>;

/** Mã lỗi → mã trạng thái HTTP. Một chỗ duy nhất, để hai route không trả hai số cho cùng một lỗi. */
export const HTTP_STATUS_OF: Record<HqErrorCode, number> = {
  HQ_UNAUTHENTICATED: 401,
  HQ_PERMISSION_DENIED: 403,
  HQ_PASSWORD_CHANGE_REQUIRED: 403,
  /** 4xx nên Cloudflare để nguyên thân lỗi (chỉ 5xx bị thay) — giao diện vẫn đọc được câu. */
  HQ_RATE_LIMITED: 429,
  HQ_NOT_FOUND: 404,
  HQ_CONNECTOR_NOT_FOUND: 404,
  HQ_METRIC_NOT_FOUND: 404,
  HQ_CONFLICT: 409,
  HQ_IDEMPOTENCY_CONFLICT: 409,
  /** 200 kèm `deduplicated` mới là đường thường; 409 chỉ khi caller ép chạy mới. */
  HQ_ALREADY_RUNNING: 409,
  HQ_VALIDATION_ERROR: 422,
  HQ_PARTNER_SET_UNTESTED: 422,
  HQ_CONNECTION_PURPOSE_LAB: 422,
  HQ_CONNECTION_IMMUTABLE_FIELD: 422,
  HQ_CONNECTION_VERSION_NOT_CERTIFIED: 422,
  /** 423 Locked — tạm thời, thử lại sau khi chuyển xong; 4xx nên Cloudflare giữ thân lỗi. */
  HQ_MIGRATION_IN_PROGRESS: 423,
  HQ_LAB_BLOCKED: 422,
  HQ_DIMENSION_NOT_ALLOWED: 422,
  HQ_SCHEMA_DRIFT: 422,
  /**
   * 424 (Failed Dependency): lỗi ở phía sau hub, không phải ở hub. KHÔNG dùng 502/503 — `api.bube-hq.com` đi qua Cloudflare,
   * Cloudflare THAY thân 5xx của origin bằng trang lỗi của nó (không CORS) ⇒ dashboard chỉ thấy « không gọi được máy chủ »,
   * mất câu lỗi thật (đo 2026-10-14: origin 502 ⇒ trình duyệt 503, status 0).
   */
  HQ_UPSTREAM_UNAVAILABLE: 424,
  HQ_INTERNAL: 500,
};
